/*
 * Store-wide cart guard: a cart may contain at most 10 units of one variant.
 *
 * This file is intentionally loaded before theme and app scripts. It protects
 * fetch, XMLHttpRequest, regular product forms, cart/add links, the Prestige
 * drawer, and Rebuy Smart Cart. Shopify must still be configured separately if
 * the same rule also needs to be enforced outside the Online Store channel.
 */
(function () {
  'use strict';

  if (window.__mountaindropVariantLimitInstalled) return;
  window.__mountaindropVariantLimitInstalled = true;

  var MAX_PER_VARIANT = 10;
  var LIMIT_MESSAGE = 'You can add a maximum of 10 of this product variant to your cart.';
  var nativeFetch = window.fetch.bind(window);
  var mutationQueue = Promise.resolve();
  var allowedForms = new WeakSet();
  var reconciliationInProgress = false;
  var alertTimer;

  function routeRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    root = root || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function cartUrl(path) {
    return routeRoot() + path.replace(/^\//, '');
  }

  function toUrl(value) {
    try {
      if (value instanceof Request) return new URL(value.url, window.location.href);
      return new URL(String(value), window.location.href);
    } catch (error) {
      return null;
    }
  }

  function cartOperation(value, method) {
    var url = toUrl(value);
    if (!url || url.origin !== window.location.origin) return null;

    var match = url.pathname.match(/\/cart\/(add|change|update)(?:\.js)?\/?$/i);
    if (!match) return null;

    var requestMethod = String(method || 'GET').toUpperCase();
    if (requestMethod !== 'POST' && !(match[1].toLowerCase() === 'add' && requestMethod === 'GET')) {
      return null;
    }

    return {
      name: match[1].toLowerCase(),
      url: url
    };
  }

  function enqueue(task) {
    var result = mutationQueue.then(task, task);
    mutationQueue = result.then(
      function () {},
      function () {}
    );
    return result;
  }

  function numberOrNull(value) {
    if (value === null || typeof value === 'undefined' || value === '') return null;
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function entriesToPayload(entries) {
    var payload = {};
    var repeated = {};
    var indexedItems = {};
    var looseItemIds = [];
    var looseItemQuantities = [];
    var updates = {};
    var updateList = [];

    entries.forEach(function (entry) {
      var key = entry[0];
      var value = entry[1];
      if (typeof value !== 'string') return;

      if (!repeated[key]) repeated[key] = [];
      repeated[key].push(value);

      var itemMatch = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (itemMatch) {
        indexedItems[itemMatch[1]] = indexedItems[itemMatch[1]] || {};
        indexedItems[itemMatch[1]][itemMatch[2]] = value;
        return;
      }

      if (key === 'id[]' || key === 'items[][id]') looseItemIds.push(value);
      if (key === 'quantity[]' || key === 'items[][quantity]') looseItemQuantities.push(value);

      var updateMatch = key.match(/^updates\[([^\]]*)\]$/);
      if (updateMatch) {
        if (updateMatch[1]) updates[updateMatch[1]] = value;
        else updateList.push(value);
      }
    });

    Object.keys(repeated).forEach(function (key) {
      payload[key] = repeated[key].length === 1 ? repeated[key][0] : repeated[key].slice();
    });

    var itemKeys = Object.keys(indexedItems).sort(function (a, b) {
      return Number(a) - Number(b);
    });
    if (itemKeys.length) payload.items = itemKeys.map(function (key) { return indexedItems[key]; });

    if (looseItemIds.length) {
      payload.items = looseItemIds.map(function (id, index) {
        return { id: id, quantity: looseItemQuantities[index] || 1 };
      });
    }

    if (Object.keys(updates).length) payload.updates = updates;
    else if (updateList.length) payload.updates = updateList;

    return payload;
  }

  async function bodyToPayload(body, contentType) {
    if (body === null || typeof body === 'undefined') return {};

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return entriesToPayload(Array.from(body.entries()));
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return entriesToPayload(Array.from(body.entries()));
    }

    if (typeof body === 'string') {
      var text = body.trim();
      if (!text) return {};

      if ((contentType || '').indexOf('json') !== -1 || /^[\[{]/.test(text)) {
        try {
          return JSON.parse(text);
        } catch (error) {
          // Some apps send URL-encoded data without its content-type.
        }
      }

      return entriesToPayload(Array.from(new URLSearchParams(text).entries()));
    }

    if (Object.prototype.toString.call(body) === '[object Object]') return body;
    return null;
  }

  function contentTypeFrom(input, init) {
    var headers = new Headers((init && init.headers) || (input instanceof Request ? input.headers : undefined));
    return headers.get('content-type') || '';
  }

  async function fetchPayload(input, init, operation) {
    var method = String((init && init.method) || (input instanceof Request && input.method) || 'GET').toUpperCase();
    if (method === 'GET') return entriesToPayload(Array.from(operation.url.searchParams.entries()));

    if (init && Object.prototype.hasOwnProperty.call(init, 'body')) {
      return bodyToPayload(init.body, contentTypeFrom(input, init));
    }

    if (input instanceof Request) {
      try {
        var clone = input.clone();
        return bodyToPayload(await clone.text(), contentTypeFrom(input, init));
      } catch (error) {
        return null;
      }
    }

    return {};
  }

  function lineVariantId(item) {
    return String(item.variant_id || item.id || '');
  }

  function cartLines(cart) {
    return Array.isArray(cart && cart.items) ? cart.items.map(function (item, index) {
      return {
        index: index,
        id: lineVariantId(item),
        key: String(item.key || ''),
        quantity: Math.max(0, numberOrNull(item.quantity) || 0)
      };
    }) : [];
  }

  function variantTotals(lines) {
    return lines.reduce(function (totals, line) {
      if (line.id) totals[line.id] = (totals[line.id] || 0) + line.quantity;
      return totals;
    }, {});
  }

  function findLine(lines, payload) {
    var lineNumber = numberOrNull(payload.line);
    if (lineNumber !== null && lines[lineNumber - 1]) return lines[lineNumber - 1];

    var identifier = payload.id === null || typeof payload.id === 'undefined'
      ? ''
      : String(payload.id);
    if (!identifier) return null;

    return lines.find(function (line) { return line.key === identifier; }) ||
      lines.find(function (line) { return line.id === identifier; }) ||
      null;
  }

  function addWouldExceed(cart, payload) {
    var totals = variantTotals(cartLines(cart));
    var requestedItems = Array.isArray(payload.items)
      ? payload.items
      : [{ id: payload.id, quantity: payload.quantity }];

    for (var index = 0; index < requestedItems.length; index += 1) {
      var item = requestedItems[index] || {};
      var id = item.id === null || typeof item.id === 'undefined' ? '' : String(item.id);
      var quantity = numberOrNull(item.quantity);
      if (!id) continue;
      quantity = quantity === null ? 1 : Math.max(0, quantity);
      totals[id] = (totals[id] || 0) + quantity;
      if (totals[id] > MAX_PER_VARIANT) return true;
    }

    return false;
  }

  function changeWouldExceed(cart, payload) {
    var lines = cartLines(cart);
    var target = findLine(lines, payload);
    var quantity = numberOrNull(payload.quantity);
    if (!target || quantity === null) return false;

    var totals = variantTotals(lines);
    totals[target.id] = (totals[target.id] || 0) - target.quantity + Math.max(0, quantity);
    return totals[target.id] > MAX_PER_VARIANT;
  }

  function updateWouldExceed(cart, payload) {
    if (payload.updates === null || typeof payload.updates === 'undefined') return false;

    var lines = cartLines(cart);
    var updates = payload.updates;

    if (Array.isArray(updates)) {
      updates.forEach(function (quantity, index) {
        var parsed = numberOrNull(quantity);
        if (lines[index] && parsed !== null) lines[index].quantity = Math.max(0, parsed);
      });
    } else if (updates && typeof updates === 'object') {
      Object.keys(updates).forEach(function (identifier) {
        var target = findLine(lines, { id: identifier });
        var parsed = numberOrNull(updates[identifier]);
        if (target && parsed !== null) target.quantity = Math.max(0, parsed);
      });
    }

    var totals = variantTotals(lines);
    return Object.keys(totals).some(function (id) { return totals[id] > MAX_PER_VARIANT; });
  }

  function wouldExceed(cart, operation, payload) {
    if (!payload) return false;
    if (operation === 'add') return addWouldExceed(cart, payload);
    if (operation === 'change') return changeWouldExceed(cart, payload);
    if (operation === 'update') return updateWouldExceed(cart, payload);
    return false;
  }

  async function currentCart() {
    var response = await nativeFetch(cartUrl('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('The cart could not be checked.');
    return response.json();
  }

  function installStyles() {
    if (!document.head || document.getElementById('md-variant-limit-styles')) return;
    var style = document.createElement('style');
    style.id = 'md-variant-limit-styles';
    style.textContent =
      '.md-variant-limit-alert{box-sizing:border-box;width:100%;margin:0 0 14px;padding:11px 14px;border:1px solid #b42318;border-radius:6px;background:#fef3f2;color:#7a271a;font:600 13px/1.4 sans-serif;text-align:left;}' +
      '.md-variant-limit-toast{position:fixed;z-index:2147483647;right:20px;bottom:20px;width:min(380px,calc(100vw - 40px));margin:0;box-shadow:0 8px 28px rgba(0,0,0,.2);}' +
      '@media(max-width:749px){.md-variant-limit-toast{right:12px;bottom:12px;width:calc(100vw - 24px);}}';
    document.head.appendChild(style);
  }

  function visible(element) {
    return Boolean(element && element.isConnected && element.getClientRects().length);
  }

  function putAlert(container, kind) {
    if (!container) return null;
    var selector = '[data-md-variant-limit-alert="' + kind + '"]';
    var alert = container.querySelector(selector);
    if (!alert) {
      alert = document.createElement('div');
      alert.className = 'md-variant-limit-alert';
      alert.setAttribute('data-md-variant-limit-alert', kind);
      alert.setAttribute('role', 'alert');
      alert.setAttribute('aria-live', 'assertive');
      container.insertBefore(alert, container.firstChild);
    }
    alert.textContent = LIMIT_MESSAGE;
    alert.hidden = false;
    return alert;
  }

  function showLimitMessage() {
    if (!document.body) return;
    installStyles();

    var visibleDrawerAlert = false;
    var themeDrawer = document.querySelector('cart-drawer');
    if (themeDrawer) {
      var themeContainer = themeDrawer.querySelector('.md-cart-drawer__body') || themeDrawer;
      putAlert(themeContainer, 'theme');
      visibleDrawerAlert = visibleDrawerAlert || visible(themeContainer);
    }

    var rebuyRoots = Array.from(document.querySelectorAll(
      '#rebuy-smart-cart, #rebuy-cart, .rebuy-smart-cart, .rebuy-cart__flyout, [data-rebuy-component="smart-cart"], [data-rebuy-cart]'
    ));
    var rebuyRoot = rebuyRoots.find(function (element) {
      return !rebuyRoots.some(function (other) { return other !== element && other.contains(element); });
    });
    if (rebuyRoot) {
      putAlert(rebuyRoot, 'rebuy');
      visibleDrawerAlert = visibleDrawerAlert || visible(rebuyRoot);
    }

    var toast = document.querySelector('[data-md-variant-limit-alert="toast"]');
    if (!visibleDrawerAlert) {
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'md-variant-limit-alert md-variant-limit-toast';
        toast.setAttribute('data-md-variant-limit-alert', 'toast');
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        document.body.appendChild(toast);
      }
      toast.textContent = LIMIT_MESSAGE;
      toast.hidden = false;
    } else if (toast) {
      toast.remove();
    }

    window.clearTimeout(alertTimer);
    alertTimer = window.setTimeout(function () {
      document.querySelectorAll('[data-md-variant-limit-alert]').forEach(function (alert) {
        alert.remove();
      });
    }, 6000);

    document.dispatchEvent(new CustomEvent('cart:variant-limit', {
      bubbles: true,
      detail: { max: MAX_PER_VARIANT, message: LIMIT_MESSAGE }
    }));
  }

  async function validate(operation, payload) {
    if (!payload) return true;
    try {
      if (wouldExceed(await currentCart(), operation, payload)) {
        showLimitMessage();
        return false;
      }
    } catch (error) {
      // A cart read failure must not turn into a store-wide add-to-cart outage.
    }
    return true;
  }

  function limitResponse() {
    return new Response(JSON.stringify({
      status: 422,
      message: 'Cart Error',
      description: LIMIT_MESSAGE
    }), {
      status: 422,
      statusText: 'Unprocessable Entity',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  window.fetch = function (input, init) {
    var method = (init && init.method) || (input instanceof Request && input.method) || 'GET';
    var operation = cartOperation(input, method);
    if (!operation) return nativeFetch(input, init);

    return enqueue(async function () {
      var payload = await fetchPayload(input, init, operation);
      if (!(await validate(operation.name, payload))) return limitResponse();
      return nativeFetch(input, init);
    });
  };

  function fakeXhrLimitResponse(xhr) {
    var responseText = JSON.stringify({
      status: 422,
      message: 'Cart Error',
      description: LIMIT_MESSAGE
    });
    var responseValue = xhr.responseType === 'json' ? JSON.parse(responseText) : responseText;

    try {
      Object.defineProperties(xhr, {
        readyState: { configurable: true, get: function () { return 4; } },
        status: { configurable: true, get: function () { return 422; } },
        statusText: { configurable: true, get: function () { return 'Unprocessable Entity'; } },
        responseText: { configurable: true, get: function () { return responseText; } },
        response: { configurable: true, get: function () { return responseValue; } }
      });
      xhr.dispatchEvent(new Event('readystatechange'));
      xhr.dispatchEvent(new ProgressEvent('load'));
      xhr.dispatchEvent(new ProgressEvent('loadend'));
    } catch (error) {
      xhr.dispatchEvent(new ProgressEvent('error'));
      xhr.dispatchEvent(new ProgressEvent('loadend'));
    }
  }

  function patchXhr(Xhr) {
    if (!Xhr || !Xhr.prototype || Xhr.prototype.__mdVariantLimitPatched) return;
    var originalOpen = Xhr.prototype.open;
    var originalSend = Xhr.prototype.send;
    if (typeof originalOpen !== 'function' || typeof originalSend !== 'function') return;

    Object.defineProperty(Xhr.prototype, '__mdVariantLimitPatched', {
      configurable: true,
      value: true
    });

    Xhr.prototype.open = function (method, url, async) {
      this.__mdVariantLimitRequest = {
        method: String(method || 'GET').toUpperCase(),
        url: url,
        async: async !== false
      };
      return originalOpen.apply(this, arguments);
    };

    Xhr.prototype.send = function (body) {
      var xhr = this;
      var request = xhr.__mdVariantLimitRequest;
      var operation = request && cartOperation(request.url, request.method);
      if (!operation || request.async === false) return originalSend.apply(xhr, arguments);

      enqueue(async function () {
        var payload = request.method === 'GET'
          ? entriesToPayload(Array.from(operation.url.searchParams.entries()))
          : await bodyToPayload(body, '');
        if (!(await validate(operation.name, payload))) {
          fakeXhrLimitResponse(xhr);
          return;
        }

        await new Promise(function (resolve) {
          xhr.addEventListener('loadend', resolve, { once: true });
          originalSend.call(xhr, body);
        });
      });
    };
  }

  // Third-party XHR wrappers ultimately delegate to this constructor. Patching
  // the native constructor once avoids nested validation queues and preserves
  // Rebuy's own request tracking.
  patchXhr(window.XMLHttpRequest);

  async function reconcile(cart) {
    if (reconciliationInProgress) return;
    reconciliationInProgress = true;

    try {
      cart = cart && Array.isArray(cart.items) ? cart : await currentCart();
      var lines = cartLines(cart);
      var grouped = lines.reduce(function (groups, line) {
        if (!groups[line.id]) groups[line.id] = [];
        groups[line.id].push(line);
        return groups;
      }, {});
      var updates = {};

      Object.keys(grouped).forEach(function (id) {
        var remaining = MAX_PER_VARIANT;
        grouped[id].forEach(function (line) {
          var allowed = Math.min(line.quantity, Math.max(0, remaining));
          if (allowed !== line.quantity && line.key) updates[line.key] = allowed;
          remaining -= allowed;
        });
      });

      if (!Object.keys(updates).length) return;

      showLimitMessage();
      var response = await nativeFetch(cartUrl('cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
      if (!response.ok) return;

      var correctedCart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: correctedCart }
      }));

      if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.setCart === 'function') {
        try {
          await window.Rebuy.SmartCart.setCart(correctedCart);
        } catch (error) {}
      }
    } catch (error) {
      // Reconciliation is a fallback for third-party code that bypasses the guards.
    } finally {
      reconciliationInProgress = false;
    }
  }

  function cartFromEvent(event) {
    var detail = event && event.detail;
    if (!detail) return null;
    if (detail.cart && Array.isArray(detail.cart.items)) return detail.cart;
    if (Array.isArray(detail.items)) return detail;
    return null;
  }

  function onCartEvent(event) {
    var cart = cartFromEvent(event);
    if (cart) reconcile(cart);
    else window.setTimeout(function () { reconcile(); }, 0);
  }

  document.addEventListener('cart:change', onCartEvent, true);
  document.addEventListener('cart:updated', onCartEvent, true);
  document.addEventListener('rebuy:cart.change', onCartEvent, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    var operation = cartOperation(form.action, form.method || 'GET');
    if (!operation || operation.name !== 'add') return;

    if (allowedForms.has(form)) {
      allowedForms.delete(form);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    var submitter = event.submitter;
    var payload = entriesToPayload(Array.from(new FormData(form).entries()));

    enqueue(function () { return validate('add', payload); }).then(function (isAllowed) {
      if (!isAllowed) return;
      allowedForms.add(form);
      if (typeof form.requestSubmit === 'function') {
        try {
          form.requestSubmit(submitter || undefined);
          return;
        } catch (error) {}
      }
      HTMLFormElement.prototype.submit.call(form);
    });
  }, true);

  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest && event.target.closest('a[href]');
    if (!link) return;
    var operation = cartOperation(link.href, 'GET');
    if (!operation || operation.name !== 'add') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    var payload = entriesToPayload(Array.from(operation.url.searchParams.entries()));
    enqueue(function () { return validate('add', payload); }).then(function (isAllowed) {
      if (isAllowed) window.location.assign(link.href);
    });
  }, true);

  document.addEventListener('DOMContentLoaded', function () {
    installStyles();
    reconcile();
  });

  window.MountainDropVariantLimit = {
    max: MAX_PER_VARIANT,
    message: LIMIT_MESSAGE,
    reconcile: reconcile,
    showMessage: showLimitMessage,
    wouldExceed: wouldExceed
  };
})();
